import { z } from "zod/v4";

import { AnalyticsViewTypeList } from "../schemas/analytics/base";

export const EstimatedTestLengthRangeSchema = z
  .object({
    low: z
      .number()
      .nullish()
      .describe("Lower bound of estimated test length in days"),
    high: z
      .number()
      .nullish()
      .describe("Upper bound of estimated test length in days"),
  })
  .describe(
    "Estimated range for how long the test should run to reach statistical significance.  Set 'high' and 'low' to null."
  );

export type EstimatedTestLengthRangeType = z.infer<
  typeof EstimatedTestLengthRangeSchema
>;

export const ExperienceAnalysisDtoSchema = z.object({
  id: z.string().describe("Unique identifier for the analysis"),
  analysisUrl: z
    .string()
    .nullable()
    .describe("URL to view detailed analysis results"),
  experienceId: z.string().describe("Experience this analysis belongs to"),
  chosenWinnerVariationId: z
    .string()
    .nullish()
    .describe("ID of the variation chosen as winner"),
  impactWinnerVariationId: z
    .string()
    .nullish()
    .describe("ID of the variation with highest statistical impact"),
  orders: z
    .number()
    .nullish()
    .describe("Total number of orders/conversions recorded"),
  impact: z.number().nullish().describe("Calculated impact/lift percentage"),
  visitors: z
    .number()
    .nullish()
    .describe("Total number of unique visitors in the test"),
  analyticsViewType: z
    .enum(AnalyticsViewTypeList)
    .describe("Type of analytics view to display"),
  excludeFromImpact: z
    .boolean()
    .default(false)
    .describe("Whether to exclude this experience from impact calculations"),
  goal: z
    .string()
    .nullish()
    .describe("Business goal or success criteria for the test"),
  controlMonthly: z
    .number()
    .nullish()
    .describe("Monthly baseline value for the control group"),
  impactMetric: z
    .string()
    .nullish()
    .describe("Primary metric used to measure impact"),
  estimatedTestLengthRange: EstimatedTestLengthRangeSchema.describe(
    "Estimated duration range for statistical significance"
  ),
});

export type ExperienceAnalysisDto = z.infer<typeof ExperienceAnalysisDtoSchema>;

export const CreateExperienceAnalysisSchema =
  ExperienceAnalysisDtoSchema.extend({
    id: z
      .uuid()
      .optional()
      .describe("Unique identifier (auto-generated if not provided)"),
    experienceId: z
      .uuid()
      .optional()
      .describe("Experience ID (optional when creating with experience)"),
  });

export type CreateExperienceAnalysis = z.infer<
  typeof CreateExperienceAnalysisSchema
>;
